import { Injectable } from '@angular/core';
import { BehaviorSubject, merge, Observable, of, ReplaySubject, Subject, Subscription } from 'rxjs';
import {
  bufferToggle,
  catchError,
  concatMap,
  debounceTime,
  filter,
  flatMap,
  map,
  tap,
  windowToggle,
} from 'rxjs/operators';

import { APIService, KPI } from '../../@core/api';
import { ErrorService } from '../../@core/utils/error.service';
import { InvestmentsService } from './investments.service';
import { NotationsService } from './notations.service';
import { CellValueChangedEvent } from 'ag-grid-community';
import { RoadmapService } from './roadmap.service';
import { InvestmentReasonsService } from './investment-reasons.service';

@Injectable()
export class InvestmentsChangesService {

  public pendingChangesNumber$ = new ReplaySubject<number>(1);
  public autoSave$ = new BehaviorSubject<boolean>(true);

  private change$: Subject<Partial<KPI.Investment>>;
  private saveChangesSubscription: Subscription;
  private save$ = new Subject<void>();
  private pendingChangesNumber: number = 0;

  constructor(
    private api: APIService,
    private errorService: ErrorService,
    private notationsService: NotationsService,
    private investmentsService: InvestmentsService,
    private roadmapService: RoadmapService,
    private investmentReasonsService: InvestmentReasonsService,
  ) {
    this.initChanges();

    // When all changes have been saved, reload the technical state notations
    this.pendingChangesNumber$
      .pipe(debounceTime(100))
      .subscribe((pendingChangesNumber) => {
        if (pendingChangesNumber === 0) {
          this.notationsService.refreshNotations();
        }
      })
      ;
  }

  public addChanges(investId: number, changes: {property: string, value: any}[]): void {
    const change: Partial<KPI.Investment> = { id: investId };
    changes.forEach((c) => change[c.property] = c.value);

    this.change$.next(change);
    this.updatePendingChanges(1);
  }

  public addChange(investId: number, property: string, value: any): void {
    const change: Partial<KPI.Investment> = { id: investId };
    change[property] = value;

    this.change$.next(change);
    this.updatePendingChanges(1);
  }

  public save(): void {
    this.save$.next();
    this.autoSave$.next(this.autoSave$.value);
  }

  public discard(): void {
    this.saveChangesSubscription.unsubscribe();
    this.initChanges();
    this.investmentsService.reloadInvestments();
  }

  public updateMultipleInvestmentsPriority(investmentsIds: number[], newPriorityId: number): Observable<number[]> {
    return this.api.investments_change_priority(investmentsIds, newPriorityId);
  }

  private initChanges(): void {
    this.change$ = new Subject<Partial<KPI.Investment>>();
    this.saveChangesSubscription = this.saveChanges();
    this.pendingChangesNumber = 0;
    this.pendingChangesNumber$.next(this.pendingChangesNumber);
  }

  private autoSaveOn$(): Observable<boolean> {
    return this.autoSave$.pipe(filter(autoSave => autoSave === true));
  }

  private autoSaveOff$(): Observable<boolean> {
    return this.autoSave$.pipe(filter(autoSave => autoSave === false));
  }

  private saveChanges(): Subscription {
    return merge(
      this.change$.pipe(bufferToggle(this.autoSaveOff$(), () => merge(this.autoSaveOn$(), this.save$))),
      this.change$.pipe(windowToggle(this.autoSaveOn$(), () => this.autoSaveOff$())),
    ).pipe(
      flatMap((investments) => investments),
      concatMap((investment) =>
        this.investmentsService.updateInvestment(
          investment.id,
          investment,
        ).pipe(
          catchError((error) => {
            this.errorService.handleError(error, true);
            return of(null);
          }),
          map((r) => r),
        ),
      ),
      tap(() => this.updatePendingChanges(-1)),
    ).subscribe(
      () => { },
    );
  }

  private updatePendingChanges(increment: number): void {
    this.pendingChangesNumber += increment;
    this.pendingChangesNumber$.next(this.pendingChangesNumber);
  }

  public changeInvestmentsYear(
    investmentIds: number[], currentYear: number, newYear: number, reason: number,
  ): Observable<number[]> {
    return this.api.investments_change_year(investmentIds, currentYear, newYear, reason);
  }

  // When a cell is changed, we need to update the backend
  // This function is called by the grid
  // It will build an array with the ids of the selected elements
  public manageBooleanCellChange(params: CellValueChangedEvent<any, any>) {

    // What data to work with
    const prefixDataMap: { [key: string]: Observable<any[]> } = {
      'impact_roadmap_indicators_': this.roadmapService.roadmapIndicators(),
      'investment_reason_': this.investmentReasonsService.investmentReasons$,
    };

    // Tell which field in backend should be updated
    const prefixFieldMap: { [key: string]: string } = {
      'impact_roadmap_indicators_': 'impact_roadmap_indicators',
      'investment_reason_': 'reasons',
    };

    // @ts-ignore
    const prefix = Object.keys(prefixDataMap).find((prefix) => params.column?.colId.includes(prefix));

    if (prefix) {
      prefixDataMap[prefix].subscribe((data: any[]) => {
        this.addChange(
          params.data.id,
          prefixFieldMap[prefix],
          data.filter((elt) => params.data[prefix + elt.id]).map((elt) => elt.id),
        );
      });
    }
  }
}
